import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, } from 'react';
import { Dialog, DialogContent, DialogActions, Button, DialogTitle, Icon, DialogContentText, TextField, makeStyles, } from '@material-ui/core';
import clsx from 'clsx';
/**
 * This is the generic style for all dialog. Each dialog can have its own style file if there
 * are any style not defined here.
 */
export const useGenericDialogStyle = makeStyles((theme) => {
    return {
        root: {},
        dialogTitle: {
            '& h2': {
                display: 'flex',
                flexDirection: 'row',
            },
        },
        dialogTitleText: {
            flex: 1,
            wordBreak: 'break-word',
        },
        dialogContent: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
        },
        dialogActionContainer: {
            marginTop: theme.spacing(1),
        },
        dialogMessage: {
            wordBreak: 'break-word',
        },
        contentIcon: {
            fontSize: '4em',
            marginBottom: theme.spacing(1),
            '&.info': {
                color: theme.palette.info.main,
            },
            '&.success': {
                color: theme.palette.success.main,
            },
            '&.warning': {
                color: theme.palette.warning.main,
            },
            '&.error': {
                color: theme.palette.error.main,
            },
            '&.confirm': {
                color: theme.palette.info.main,
            },
            '&.input': {
                color: theme.palette.info.main,
            },
        },
    };
}, {
    name: 'GenericDialog',
});
const translateMessage = (message, messageData, translator) => {
    if (message && translator) {
        // if there is 'titleData', translate the 'titleData' fields first
        const transData = messageData
            ? Object.entries(messageData).reduce((result, entry) => {
                const [key, value] = entry;
                return {
                    ...result,
                    [key]: (typeof value === 'string') ? translator(value) : value,
                };
            }, {})
            : {};
        return translator(message, transData);
    }
    return message;
};
export var GenericDialogResponseCode;
(function (GenericDialogResponseCode) {
    GenericDialogResponseCode[GenericDialogResponseCode["NEGATIVE"] = 0] = "NEGATIVE";
    GenericDialogResponseCode[GenericDialogResponseCode["POSITIVE"] = 1] = "POSITIVE";
})(GenericDialogResponseCode || (GenericDialogResponseCode = {}));
export var GenericDialogType;
(function (GenericDialogType) {
    GenericDialogType["INFO"] = "info";
    GenericDialogType["SUCCESS"] = "success";
    GenericDialogType["WARNING"] = "warning";
    GenericDialogType["ERROR"] = "error";
    GenericDialogType["CONFIRM"] = "confirm";
    GenericDialogType["INPUT"] = "input";
})(GenericDialogType || (GenericDialogType = {}));
const iconsByType = {
    [GenericDialogType.INFO]: 'info',
    [GenericDialogType.SUCCESS]: 'check_circle',
    [GenericDialogType.WARNING]: 'warning',
    [GenericDialogType.ERROR]: 'report',
    [GenericDialogType.CONFIRM]: 'help',
};
/**
 * This dialog can be used for display alerts of different types, INFO, WARNING, SUCCESS, ERROR, etc...
 * Everything in the dialog are customizable e.g. title, message, and buttons.
 */
export const GenericDialog = (props) => {
    const { dialogOptions } = props;
    const dialogClasses = useGenericDialogStyle();
    const inputRef = React.useRef();
    /**
     * Translate the values of the title, message, button' data before we pass it to interpolation
     *
     */
    const { dialogTitle, dialogMessage, inputName, inputError, inputPlaceholder, positiveButton, negativeButton } = useMemo(() => {
        var _a, _b, _c, _d, _e, _f;
        const translatedMessages = {
            dialogTitle: translateMessage(dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.title, dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.titleData, props.translator),
            dialogMessage: translateMessage(dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.message, dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.messageData, props.translator),
            inputName: translateMessage((_a = dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.input) === null || _a === void 0 ? void 0 : _a.name, (_b = dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.input) === null || _b === void 0 ? void 0 : _b.translationData, props.translator),
            inputError: translateMessage((_c = dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.input) === null || _c === void 0 ? void 0 : _c.error, (_d = dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.input) === null || _d === void 0 ? void 0 : _d.translationData, props.translator),
            inputPlaceholder: translateMessage((_e = dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.input) === null || _e === void 0 ? void 0 : _e.placeholder, (_f = dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.input) === null || _f === void 0 ? void 0 : _f.translationData, props.translator),
            positiveButton: translateMessage(dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.positiveButton, dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.positiveButtonData, props.translator),
            negativeButton: translateMessage(dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.negativeButton, dialogOptions === null || dialogOptions === void 0 ? void 0 : dialogOptions.negativeButtonData, props.translator),
        };
        return translatedMessages;
    }, [dialogOptions, props.translator]);
    const handleClose = (response) => {
        if (props.onClose) {
            props.onClose(response);
        }
    };
    const onPositiveButtonClicked = () => {
        if (dialogOptions && dialogOptions.type === GenericDialogType.INPUT) {
            const value = inputRef.current.value.trim();
            handleClose({
                code: GenericDialogResponseCode.POSITIVE,
                input: value,
            });
        }
        else {
            handleClose({
                code: GenericDialogResponseCode.POSITIVE,
            });
        }
    };
    const onNegativeButtonClicked = () => {
        handleClose({
            code: GenericDialogResponseCode.NEGATIVE,
        });
    };
    return (_jsxs(Dialog, { PaperProps: {
            className: dialogOptions.className,
            style: dialogOptions.style,
        }, fullWidth: true, disableEscapeKeyDown: dialogOptions ? dialogOptions.disableEscapeKeyDown : false, open: dialogOptions !== undefined, onClose: (event, reason) => {
            if (reason !== 'backdropClick' || (dialogOptions && dialogOptions.disableBackdropClick === false)) {
                handleClose();
            }
        }, children: [_jsxs(DialogTitle, { className: dialogClasses.dialogTitle, children: [_jsx("div", { className: dialogClasses.dialogTitleText, children: dialogTitle }), props.languageSelectorComponent] }), dialogOptions && dialogOptions.message
                && (_jsxs(DialogContent, { className: dialogClasses.dialogContent, children: [dialogOptions && dialogOptions.type && iconsByType[dialogOptions.type]
                            && _jsx(Icon, { className: clsx(dialogClasses.contentIcon, dialogOptions.type), children: iconsByType[dialogOptions.type] }), _jsx(DialogContentText, { className: dialogClasses.dialogMessage, children: dialogMessage })] })), dialogOptions && dialogOptions.type === GenericDialogType.INPUT && dialogOptions.input
                && (_jsx(DialogContent, { className: dialogClasses.dialogContent, children: _jsx(TextField, { variant: "standard", type: dialogOptions.input.type, required: dialogOptions.input.required !== false, fullWidth: true, autoFocus: true, label: inputName, InputLabelProps: {
                            shrink: true,
                        }, inputProps: {
                            ref: inputRef,
                            placeholder: inputPlaceholder,
                        }, error: Boolean(dialogOptions.input.error), helperText: inputError, defaultValue: dialogOptions.input.defaultValue, onKeyPress: (event) => {
                            if (event.key === 'Enter') {
                                onPositiveButtonClicked();
                            }
                        } }) })), dialogOptions && (dialogOptions.negativeButton || dialogOptions.positiveButton)
                && (_jsxs(DialogActions, { children: [dialogOptions.negativeButton
                            && (_jsx(Button, { onClick: onNegativeButtonClicked, children: negativeButton })), dialogOptions.positiveButton
                            && (_jsx(Button, { type: "submit", variant: "contained", onClick: onPositiveButtonClicked, color: "primary", autoFocus: dialogOptions.type !== GenericDialogType.INPUT, children: positiveButton }))] }))] }));
};
